import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MessageCircle, Trophy, Users, GraduationCap } from "lucide-react";
import { api } from "../lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1692288720754-743fbd1f2155?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHx0ZW5uaXMlMjBjb3VydCUyMGRhcmt8ZW58MHx8fHwxNzg3OTAyNTg5fDA&ixlib=rb-4.1.0&q=85";
const AERIAL_IMG = "https://images.unsplash.com/photo-1499510318569-1a3d67dc3976?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjB0ZW5uaXMlMjBmYWNpbGl0eXxlbnwwfHx8fDE3ODc5MDI1ODl8MA&ixlib=rb-4.1.0&q=85";
const ACTION_IMG = "https://images.unsplash.com/photo-1554068865-24cecd4e34b8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHx0ZW5uaXMlMjBwbGF5ZXIlMjBhY3Rpb258ZW58MHx8fHwxNzg3OTAyNTg5fDA&ixlib=rb-4.1.0&q=85";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const highlights = [
  { icon: Trophy, title: "Championship Courts", text: "Six surfaces — indoor hard, red clay, floodlit night play and true grass — maintained to tournament spec.", to: "/courts", cta: "Explore courts" },
  { icon: GraduationCap, title: "World-Class Coaching", text: "Ex-tour pros and academy directors. Serve labs, clay strategy, junior pathways and footwork science.", to: "/coaching", cta: "Meet the coaches" },
  { icon: Users, title: "A Club That Fits", text: "Three membership tiers from casual rallies to unlimited everything. Compare and join in two clicks.", to: "/membership", cta: "See memberships" },
];

export default function Home() {
  const [stats, setStats] = useState([]);
  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      {/* Hero */}
      <section className="relative min-h-[90vh] [min-height:90dvh] flex items-end overflow-hidden">
        <img src={HERO_IMG} alt="Floodlit dark blue tennis court at night" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pb-24 pt-40 w-full">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="text-[#ccff00] uppercase tracking-[0.3em] text-sm mb-6" data-testid="hero-kicker">
            The Point Strategy Hub · Demo Club
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-display uppercase text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight max-w-4xl"
            data-testid="hero-title"
          >
            Every point is <span className="text-[#ccff00]">a strategy.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6 }} className="text-white/70 text-base md:text-lg max-w-xl mt-8 leading-relaxed">
            Six championship courts, tour-level coaching and an AI concierge that speaks your language. Book a court in seconds — it's all a free demo.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.6 }} className="flex flex-wrap gap-4 mt-10">
            <Link
              to="/courts"
              data-testid="hero-book-cta"
              className="bg-[#ccff00] text-black font-bold uppercase tracking-wider px-8 py-4 rounded-full hover:bg-[#a3e635] hover:-translate-y-1 transition-transform duration-200 inline-flex items-center gap-2"
            >
              Book a Court <ArrowRight size={18} />
            </Link>
            <Link
              to="/assistant"
              data-testid="hero-ai-cta"
              className="border border-white/25 text-white uppercase tracking-wider font-semibold px-8 py-4 rounded-full hover:border-[#ccff00]/70 hover:text-[#ccff00] transition-colors duration-200 inline-flex items-center gap-2"
            >
              <MessageCircle size={18} /> Ask the AI
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-white/10 bg-[#0f0f10]" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid grid-cols-2 lg:grid-cols-4 gap-10">
          {stats.map((s, i) => (
            <motion.div key={s.label} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
              <p className="font-display text-4xl lg:text-5xl text-[#ccff00]" data-testid={`stat-value-${i}`}>{s.value}</p>
              <p className="text-white/50 uppercase tracking-widest text-xs mt-2">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Highlights */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="highlights-section">
        <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="font-display uppercase text-3xl lg:text-5xl tracking-tight mb-16 max-w-2xl">
          Built for players who <span className="text-[#ccff00]">keep score.</span>
        </motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {highlights.map((h, i) => (
            <motion.div
              key={h.title}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              transition={{ delay: i * 0.12 }}
              className="bg-[#0f0f10] border border-[#2a2a2a] rounded-md p-8 hover:-translate-y-1 hover:border-[#ccff00]/50 transition-all duration-300"
              data-testid={`highlight-card-${i}`}
            >
              <h.icon className="text-[#ccff00] mb-6" size={32} />
              <h3 className="font-display uppercase text-xl tracking-tight mb-3">{h.title}</h3>
              <p className="text-white/60 leading-relaxed mb-6">{h.text}</p>
              <Link to={h.to} className="text-[#ccff00] text-sm uppercase tracking-widest inline-flex items-center gap-2 hover:gap-3 transition-[gap] duration-200">
                {h.cta} <ArrowRight size={14} />
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Facility banner */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24" data-testid="facility-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="lg:col-span-7 relative rounded-md overflow-hidden min-h-[320px] group">
            <img src={AERIAL_IMG} alt="Aerial view of tennis courts surrounded by trees" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-black/40" />
            <div className="absolute bottom-0 p-8">
              <p className="font-display uppercase text-2xl tracking-tight">12 acres of pure tennis</p>
              <p className="text-white/70 mt-2">Tree-lined outdoor courts, indoor arena, gym and recovery suite.</p>
            </div>
          </motion.div>
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} transition={{ delay: 0.15 }} className="lg:col-span-5 relative rounded-md overflow-hidden min-h-[320px] group">
            <img src={ACTION_IMG} alt="Tennis player hitting a forehand mid-rally" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-black/40" />
            <div className="absolute bottom-0 p-8">
              <p className="font-display uppercase text-2xl tracking-tight">Train like the tour</p>
              <Link to="/coaching" className="text-[#ccff00] text-sm uppercase tracking-widest inline-flex items-center gap-2 mt-2">
                Coaching programs <ArrowRight size={14} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/10 bg-[#0f0f10]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="font-display uppercase text-3xl lg:text-5xl tracking-tight max-w-xl">
            Ready to take <span className="text-[#ccff00]">the point?</span>
          </motion.h2>
          <Link
            to="/membership"
            data-testid="home-membership-cta"
            className="bg-[#ccff00] text-black font-bold uppercase tracking-wider px-10 py-4 rounded-full hover:bg-[#a3e635] hover:-translate-y-1 transition-transform duration-200 inline-flex items-center gap-2"
          >
            Join the Club <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </main>
  );
}
